import { Box, Button, createStyles, FileButton, Image, LoadingOverlay } from "@mantine/core";
import { IconPhotoOff, IconPhotoPlus } from "@tabler/icons";
import React from "react";
import { AuthApi } from "../../apis";
import { Message } from "../../utils/message";
import HttpHandler from "../../utils/http-handler";

export type IFile = {
  uploading: boolean;
  url?: string;
  file: Blob | null;
};

type Props = {
  value?: string | undefined | null;
  error?: string;
  onChange?: (file: IFile) => void;
  size?: number;
  radius?: number;
};

export function ImageUploadMe({ onChange, error, value, size, radius }: Props) {
  const { classes, cx } = useStyle();
  const [file, setFile] = React.useState<IFile | null>(null);
  const resetRef = React.useRef<() => void>(null);

  const onFileUpload = (blob: Blob | null) => {
    if (!blob) return;

    if (blob.size < 1048576) {
      (async () => {
        try {
          const form = new FormData();
          form.append("file", blob);

          const res = await AuthApi.uploadImage(form);

          if (onChange)
            onChange({
              file: blob,
              url: res.url,
              uploading: false,
            });

          setFile({
            file: blob,
            url: res.url,
            uploading: false,
          });
        } catch (err: any) {
          setFile(null);
          Message.error((err as HttpHandler)?.message!);
        }
      })();

      setFile({
        file: blob,
        uploading: true,
      });
    } else {
      Message.error("Файлын хэмжээ хэтэрсэн байна!");

      setFile({
        file: blob,
        uploading: false,
      });
    }
  };

  React.useEffect(() => {
    value &&
      setFile({
        file: null,
        url: value,
        uploading: false,
      });
  }, [value]);

  return (
    <div className={classes.multiImageUpload}>
      <FileButton resetRef={resetRef} onChange={onFileUpload} accept="image/png,image/jpeg">
        {(props) => {
          return file ? (
            <Box sx={{ borderRadius: radius ? radius : 4 }} w={size ? size : 80} h={size ? size : 80} className={cx(classes.imageBox)} {...props}>
              <Image
                withPlaceholder
                placeholder={<IconPhotoOff />}
                className={classes.image}
                width={size ? size : 80}
                height={size ? size : 80}
                radius={radius}
                src={file?.file ? URL.createObjectURL(file.file) : file.url}
                alt="image"
              />
              <LoadingOverlay visible={file.uploading} overlayOpacity={0.3} loaderProps={{ size: "sm" }} />
            </Box>
          ) : (
            <Button
              radius={radius}
              sx={{ width: size ? size : 80, height: size ? size : 80 }}
              variant="light"
              className={cx(classes.uploadButton, error && "error")}
              {...props}>
              <IconPhotoPlus size={20} />
            </Button>
          );
        }}
      </FileButton>
    </div>
  );
}

const useStyle = createStyles((theme, _params, getRef) => ({
  multiImageUpload: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 5,
    gap: 15,
  },
  imageBox: {
    border: `1px solid ${theme.colors.gray[4]}`,
    display: "flex",
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    overflow: "hidden",
    "&.selected": {
      borderWidth: 3,
      borderStyle: "solid",
      borderColor: theme.colors.indigo[6],
    },
    [`&:hover .${getRef("actionBox")}`]: {
      display: "flex!important",
    },
  },
  image: {
    position: "absolute",
  },
  uploadButton: {
    cursor: "pointer",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    "&.error": {
      backgroundColor: theme.colors.red[1],
      color: theme.colors.red[6],
    },
  },
}));
